import type { IOptionsComposableVueQueryOptions } from '@/types/shared/vue-query'
import type { IModel } from '@/types/rc/miners'
import { getControlBoardModels } from '@/services/core.service'
import { generateModelDisplayName, generateModelValueKey } from '@/util/rc'
import { useQuery } from '@tanstack/vue-query'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types.mjs'
import { ref } from 'vue'

/**
 * Composable for getControlBoardModels response, mapped to ElSelect options.
 *
 * @param refetchOnWindowFocus default: true; useQuery refetch on window focus.
 * @param refetchOnMount default: true; useQuery refetch on mount.
 * @returns getControlBoardModels useQuery state.
 */
export const useCBModelOptions = (
  { refetchOnWindowFocus, refetchOnMount, enabled }: IOptionsComposableVueQueryOptions = {
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: ref(true),
  }
) => {
  const { isFetching, isRefetching, isError, data, error, refetch } = useQuery({
    queryKey: ['control-board-model-options'],
    queryFn: async () => {
      const cbModels = await getControlBoardModels()
      return cbModels.map((cbModel: IModel) => {
        return {
          label: generateModelDisplayName(cbModel.name!, cbModel.version),
          value: {
            ...cbModel,
            valueKey: generateModelValueKey(cbModel.name!, cbModel.version),
          },
        } as OptionType
      })
    },
    refetchOnWindowFocus,
    refetchOnMount,
    enabled,
  })

  return {
    isFetching,
    isRefetching,
    isError,
    data,
    error,
    refetch,
  }
}
